import React from 'react';
import { createBrowserRouter, redirect, type RouteObject } from 'react-router-dom';
import AppBootstrap from '@root/app/routing/layouts/AppBootstrap';
import AuthLayout from '@root/app/routing/layouts/AuthLayout';
import ProtectedLayout from '@root/app/routing/layouts/ProtectedLayout';
import RoleGuard from '@root/app/routing/RoleGuard';
import { protectedRouteLoader } from '@root/app/routing/guards';
import { ROUTE_MANIFEST } from '@root/app/routing/routeManifest';
import * as Pages from '@root/app/routing/routeComponents';

const toRelativePath = (absolutePath: string): string => absolutePath.replace(/^\//, '');

const STATE_COMPONENT: Record<string, React.ComponentType> = {
    'forgot': Pages.ForgotPasswordPage,
    'require': Pages.ForgotPasswordPage,
    'callback': Pages.CallbackPage,
    'private.dashboard': Pages.DashboardPage,
    'overview.dashboard': Pages.OverviewPage,
    'actions.list': Pages.ActionsPage,
    'announcements.list': Pages.AnnouncementsPage,
    'rewards.home': Pages.RewardsPage,
    'engagement.screen': Pages.EngagementPage,
    'topics.browse': Pages.TopicsPage,
    'feedback.detail': Pages.FeedbackPage,
    'feedback.comments': Pages.FeedbackCommentsPage,
    'feedback.conversations': Pages.ConversationsPage,
    'feedback.ideas': Pages.FeedbackIdeasPage,
    'feedback.initiativeDetail': Pages.FeedbackInitiativeDetailPage,
    'feedback.recognition': Pages.FeedbackRecognitionPage,
    'surveys.list': Pages.SurveysPage,
    'surveys.selector': Pages.SurveysSelectorPage,
    'surveys.edit': Pages.SurveysEditPage,
    'surveys.results': Pages.SurveyResultsPage,
    'manage.users': Pages.ManageUsersPage,
    'manage.users_bulk': Pages.ManageUsersBulkPage,
    'manage.shift': Pages.ShiftPage,
    'manage.timetracking': Pages.TimeTrackingPage,
    'focus_roles': Pages.FocusRolesPage,
    'company.config': Pages.SettingsPage,
    'reports.activation': Pages.ReportsActivationPage,
    'reports.participation': Pages.ReportsParticipationPage,
    'reports.ranking': Pages.ReportsRankingPage,
    'custom.lc': Pages.LifeCyclePage,
    'journey.screen': Pages.LifeCyclePage,
    'advisors.surveys': Pages.AdvisorsSurveysPage,
    'advisors.survey-edit': Pages.AdvisorsSurveysEditPage,
    'advisors.templates': Pages.AdvisorsTemplatesPage,
    'advisors.company_create': Pages.AdvisorsCompanyCreatePage,
    'advisors.employee_locator': Pages.AdvisorsEmployeeLocatorPage,
    'advisors.management': Pages.AdvisorsManagementPage,
    'advisors.absences': Pages.AdvisorsAbsencesPage,
    'design.system': Pages.DesignSystemPage
};

const buildRoute = (
    route: (typeof ROUTE_MANIFEST)[number],
    Component: React.ComponentType
): RouteObject => ({
    path: toRelativePath(route.path),
    element: <Component />,
    handle: {
        ...route.meta,
        routeName: route.name
    }
});

const shellRoutes: RouteObject[] = ROUTE_MANIFEST
    .filter((route) => route.layout === 'shell')
    .map((route) => {
        const Component = STATE_COMPONENT[route.name];
        if (!Component) {
            return null;
        }
        return buildRoute(route, Component);
    })
    .filter(Boolean) as RouteObject[];

const authStaticRoutes: RouteObject[] = ROUTE_MANIFEST
    .filter((route) => route.layout === 'auth'
        && !route.path.startsWith('/onboarding')
        && route.path !== '/access/*')
    .map((route) => {
        const Component = STATE_COMPONENT[route.name];
        if (!Component) {
            return null;
        }
        return buildRoute(route, Component);
    })
    .filter(Boolean) as RouteObject[];

export const router = createBrowserRouter([
    {
        element: <AppBootstrap />,
        children: [
            {
                element: <AuthLayout />,
                children: [
                    ...authStaticRoutes,
                    {
                        path: 'onboarding/*',
                        element: <Pages.OnboardingPage />,
                        handle: { public: true, routeName: 'onboarding' }
                    },
                    {
                        path: 'access/*',
                        element: <Pages.AccessPage />,
                        handle: { public: true, routeName: 'access' }
                    }
                ]
            },
            {
                element: <ProtectedLayout />,
                loader: protectedRouteLoader,
                children: [
                    {
                        element: <RoleGuard />,
                        children: shellRoutes
                    }
                ]
            },
            {
                path: '*',
                loader: () => redirect('/onboarding')
            }
        ]
    }
]);
